import React, { useEffect, useRef } from 'react';
import { produce } from 'immer';
import {
  CheckboxGroup,
  DateInput,
  Form,
  FormData,
  FormErrors,
  FormInstance,
  RadioGroup,
  SubmitButton,
  TextArea
} from '@lightbeamhealth/blueprint-form';
import moment from 'moment-timezone';
import { faMoon } from '@fortawesome/pro-solid-svg-icons';
import { useMetaData } from '../../../../contexts/metadata';
import { useTriage } from '../../../../contexts/triage';
import { IAlert } from '../../../../libs/alert';
import { RequestRunner } from '../../../../libs/hooks/useRequestJSON';
import { IPatient } from '../../../../libs/patient';
import { PostJSON } from '../../../../libs/request';
import { GetCurrentTZ } from '../../../../libs/time';
import Icon from '../../../atoms/icon';
import Callout from '../../callout';
import ErrorCallout from '../../callout/error-callout';
import { Response } from './types';
import {
  deserializeSnoozeFormData,
  serializeSnoozeFormData,
  getSnoozeDateFromDuration,
  getSnoozeDurationOptions,
  SnoozeFormData,
  SnoozeFormDataRaw
} from '../../../../libs/event';

interface SnoozeFormProps {
  addressScope: 'alert' | 'patient';
  patient: IPatient;
  alert?: IAlert;
  selectedAlertIds: number[];
  note: string;
  options: any;
  data: Response;
  initialFormData: any;
  error: any;
  isLoading: boolean;
  run: RequestRunner;
  onSnoozed: (until?) => void;
  onNoteChanged: (note: string) => void;
}

const validate = (values: FormData): FormErrors => {
  const errors: FormErrors = {};
  if (!values.reason.length) {
    errors.reason = 'You must select one of the reasons!';
  }
  if (!values.snoozeUntil && !(parseInt(values.duration) > 0)) {
    errors.duration = 'You must select a duration to snooze or pick a date!';
  }
  // Alert selection validation occurs within <AddressEvents />
  return errors;
};

const SnoozeForm: React.FC<SnoozeFormProps> = ({
  addressScope,
  patient,
  selectedAlertIds,
  options,
  data,
  initialFormData,
  error,
  isLoading,
  note,
  run,
  onSnoozed,
  onNoteChanged
}) => {
  const { handleToggleSnoozeAlert, handleToggleSnoozePatient } = useTriage();
  const { terms } = useMetaData();
  const initialData: SnoozeFormData = {
    duration: '1-days',
    snoozeUntil: null,
    reason: options.reason[0],
    note: '',
    tz: GetCurrentTZ(),
    misc: ''
  };
  const initData = initialFormData ? initialFormData : initialData;
  const formDataRef = useRef(initData);

  // Update labels based on addressScope
  let snoozeForLabel = 'Snooze Alert(s) for';
  let reasonForSnoozeLabel = 'Reason for Snoozing Alert(s)';
  let snoozeSuccessLabel = 'Alert(s) successfully snoozed';
  if (addressScope === 'patient') {
    snoozeForLabel = `Snooze ${terms.patient.capitalized} for`;
    reasonForSnoozeLabel = `Reason for Snoozing ${terms.patient.capitalized}`;
    snoozeSuccessLabel = `${patient.firstName} successfully snoozed`;
  }

  const form = new FormInstance<SnoozeFormDataRaw, SnoozeFormData>(
    deserializeSnoozeFormData(initData),
    {
      serialize: serializeSnoozeFormData,
      deserialize: deserializeSnoozeFormData
    }
  );
  const requestStatus = data ? data.status : null;

  function submit(data: SnoozeFormData) {
    const url =
      addressScope === 'alert' ? '/alert/snooze/' : '/patient/snooze/';
    const params: SnoozeFormData = produce(data, (draft) => {
      if (addressScope === 'alert') {
        // Snoozing one or more alerts
        if (!draft.ids || !draft.ids.length) {
          draft.ids = null; // Don't add default alert.id anymore; force endpoint to error
        }
      } else {
        // Snoozing whole patient and all alerts by default
        draft.id = patient.id;
        if (draft.ids) {
          delete draft.ids; // Remove alert ids
        }
      }
      let snoozeUntil = draft.snoozeUntil;
      if (!snoozeUntil) {
        // NOTE: Might be confusing, snoozing until 12:00 AM
        snoozeUntil = getSnoozeDateFromDuration(draft.duration);
      }
      draft.snoozeUntil = snoozeUntil;
      // Remove duration b/c don't need to pass that to request
      delete draft.duration;
    });
    const req = () =>
      PostJSON<Response, SnoozeFormData>({
        url,
        params
      });
    run(req);
  }

  useEffect(() => {
    // When request returns, update UI
    if (requestStatus === 'OK') {
      if ('patient' in data) {
        if (data.patient) {
          const patient = {
            ...data.patient,
            _justSnoozed: true
          };
          const alerts = [];
          if (data.alerts) {
            for (const alert of data.alerts) {
              alerts.push({
                ...alert,
                _justSnoozed: true
              });
            }
          }
          handleToggleSnoozePatient(patient, alerts);
          onSnoozed();
          return;
        }
      }
      if ('alerts' in data) {
        if (data.alerts) {
          const alerts = data.alerts.map((alert: IAlert) => {
            return {
              ...alert,
              _justSnoozed: true
            };
          });
          handleToggleSnoozeAlert(alerts);
          onSnoozed();
          return;
        }
      }
    }
  }, [requestStatus]);

  // Update alert ids whenever selectedAlertIds change
  useEffect(() => {
    form.setFieldValue('ids', selectedAlertIds);
  }, [selectedAlertIds]);

  // Update note is a new note is sent down
  useEffect(() => {
    form.setFieldValue('note', note);
  }, [note]);

  useEffect(() => {
    return () => {
      // Update note only on unmount. onChange did not fire fast enough.
      onNoteChanged(formDataRef.current.note);
    };
  }, []);

  const onChange = (data: SnoozeFormData) => {
    const prevData = formDataRef.current;
    if (prevData.snoozeUntil == null && data.snoozeUntil != null) {
      if (data.duration != null) {
        form.setData(
          produce(data, (draft) => {
            draft.duration = null;
          })
        );
      }
    } else if (prevData.duration == null && data.duration != null) {
      if (data.snoozeUntil != null) {
        form.setData(
          produce(data, (draft) => {
            draft.snoozeUntil = null;
          })
        );
      }
    }
    formDataRef.current = data;
  };

  // Extract confirm button disabled boolean
  const isConfirmDisabled =
    isLoading ||
    (addressScope === 'alert' &&
      (!selectedAlertIds || selectedAlertIds?.length === 0));

  return (
    <div>
      {
        /**
         * WCAG: Error Callouts must be placed above related form elements
         **/
        error && <ErrorCallout className="my-7">{error}</ErrorCallout>
      }
      <Form
        form={form}
        validate={validate}
        onSubmit={submit}
        onChange={onChange}
      >
        <RadioGroup
          label={snoozeForLabel}
          name="duration"
          options={getSnoozeDurationOptions()}
        />
        <DateInput
          label="Choose Date"
          name="snoozeUntil"
          min={moment().add(1, 'day').startOf('day').toDate()}
          fill
        />
        <RadioGroup
          label={reasonForSnoozeLabel}
          name="reason"
          options={options.reason.map((opt: any) => {
            return {
              label: opt,
              value: opt
            };
          })}
        />
        <h4 className={options?.misc?.length > 0 ? 'mb-0' : ''}>
          Additional Notes
        </h4>
        {options?.misc?.length > 0 && (
          <CheckboxGroup
            name="misc"
            options={options.misc.map((misc: any) => {
              return {
                label: misc.label,
                value: misc.value
              };
            })}
            required
          />
        )}
        <TextArea
          style={{
            height: '300px'
          }}
          className="text-small"
          aria-label="Additional Note"
          placeholder="Add additional notes here..."
          name="note"
          fill
        />
        {
          // NOTE: Should this be a Toast?
          requestStatus === 'OK' && (
            <Callout intent="success" className="my-7">
              {snoozeSuccessLabel}
            </Callout>
          )
        }
        <SubmitButton fill disabled={isConfirmDisabled} className="mb-6">
          <Icon icon={faMoon} /> Confirm Snooze
        </SubmitButton>
      </Form>
    </div>
  );
};

export default SnoozeForm;
